import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Card, Field, Input, Spinner } from '@mkcore/ui';
import logo from '@mkcore/ui/assets/MKC-logo-secondary.png';
import { meQuery } from '@/lib/session';

const search = z.object({ redirect: z.string().optional() }).loose();

/**
 * Sign-in (CO-0004): Entra ID only in production; the dev login exists only in dev and ci. When an MCP client
 * (Claude) sends the user here during OAuth, the query carries the signed authorize request and we continue it
 * after sign-in (CO-0110).
 */
export const Route = createFileRoute('/login')({
  validateSearch: search,
  component: Login,
});

function continueTarget(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.has('client_id') && params.has('sig')) return `/api/auth/oauth2/authorize?${params.toString()}`;
  const r = params.get('redirect');
  return r && r.startsWith('/') && !r.startsWith('//') ? r : '/';
}

function Login() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const options = useQuery({
    queryKey: ['login-options'],
    queryFn: async () => (await fetch('/api/login-options')).json() as Promise<{ entra: boolean; devLogin: boolean }>,
  });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const afterSignIn = async () => {
    const target = continueTarget();
    // Drop the cached "not signed in" answer; the app layout then loads the caller fresh.
    qc.removeQueries({ queryKey: meQuery.queryKey });
    if (target.startsWith('/api/')) window.location.assign(target);
    else await navigate({ to: target });
  };

  const microsoft = async () => {
    setBusy(true);
    const res = await fetch('/api/auth/sign-in/social', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ provider: 'microsoft', callbackURL: continueTarget(), errorCallbackURL: '/login?error=1' }),
    });
    const body = (await res.json().catch(() => ({}))) as { url?: string };
    if (body.url) window.location.assign(body.url);
    else {
      setBusy(false);
      setError(t('auth.failed'));
    }
  };

  const devLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await fetch('/api/auth/dev-login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ email }),
    });
    setBusy(false);
    if (!res.ok) return setError(t('auth.failed'));
    await afterSignIn();
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--mkc-navy-950)] p-4">
      <Card className="w-full max-w-sm overflow-hidden">
        <div className="bg-[var(--mkc-navy-850)] px-8 py-6">
          <img src={logo} alt="MKC · Martijn Kerver Consulting" className="mx-auto w-44" />
        </div>
        <div className="grid gap-5 p-8">
          <div className="grid gap-1 text-center">
            <h1 className="text-xl font-bold">{t('auth.title')}</h1>
            <p className="text-sm text-fg-2">{t('auth.subtitle')}</p>
          </div>
          {options.isLoading && <Spinner label={t('common.loading')} />}
          {error && (
            <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}
          {options.data?.entra && (
            <Button size="lg" onClick={() => void microsoft()} loading={busy}>
              {t('auth.microsoft')}
            </Button>
          )}
          {options.data?.devLogin && (
            <form onSubmit={(e) => void devLogin(e)} className="grid gap-3 rounded-md border border-dashed border-line p-4">
              <p className="text-sm font-bold">{t('auth.devTitle')}</p>
              <Field label={t('common.email')} hint={t('auth.devHint')}>
                {(p) => <Input {...p} type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />}
              </Field>
              <Button type="submit" variant="secondary" loading={busy}>
                {t('auth.devButton')}
              </Button>
            </form>
          )}
          {options.data && !options.data.entra && !options.data.devLogin && <p className="text-sm text-fg-2">{t('auth.noMethods')}</p>}
        </div>
      </Card>
    </main>
  );
}
